/**
 * Payment Trend Chart Component
 * Displays a line chart showing payment trends over time
 */

interface PaymentTrendData {
  month: string;
  totalCharges: number;
  totalCollected: number;
}

interface PaymentTrendChartProps {
  data: PaymentTrendData[];
}

export function PaymentTrendChart({ data }: PaymentTrendChartProps) {
  if (data.length === 0) {
    return (
      <div className="p-8 text-center text-muted-foreground">
        No trend data available
      </div>
    );
  }

  // Calculate max value for scaling
  const maxValue = Math.max(
    ...data.flatMap((d) => [d.totalCharges, d.totalCollected])
  );
  const chartHeight = 300;
  const chartWidth = 800;
  const padding = 40;

  // Calculate points for lines
  const xStep = (chartWidth - padding * 2) / (data.length - 1);
  const yScale = (chartHeight - padding * 2) / maxValue;

  const chargesPoints = data
    .map((d, i) => {
      const x = padding + i * xStep;
      const y = chartHeight - padding - d.totalCharges * yScale;
      return `${x},${y}`;
    })
    .join(' ');

  const collectedPoints = data
    .map((d, i) => {
      const x = padding + i * xStep;
      const y = chartHeight - padding - d.totalCollected * yScale;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <div>
      {/* Legend */}
      <div className="flex gap-4 mb-4 justify-center">
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-primary" />
          <span className="text-sm text-muted-foreground">Charges</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-0.5 bg-success" />
          <span className="text-sm text-muted-foreground">Collected</span>
        </div>
      </div>

      {/* Chart */}
      <svg
        width="100%"
        height={chartHeight}
        viewBox={`0 0 ${chartWidth} ${chartHeight}`}
        style={{ display: 'block' }}
      >
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((fraction) => {
          const y = chartHeight - padding - maxValue * fraction * yScale;
          return (
            <g key={fraction}>
              <line
                x1={padding}
                y1={y}
                x2={chartWidth - padding}
                y2={y}
                className="stroke-border dark:stroke-muted-foreground"
                strokeDasharray="2 4"
                strokeWidth="1"
              />
              <text
                x={padding - 10}
                y={y + 5}
                textAnchor="end"
                className="fill-muted-foreground"
                fontSize="12"
              >
                ${((maxValue * fraction) / 100).toFixed(0)}
              </text>
            </g>
          );
        })}

        {/* Charges line */}
        <polyline
          points={chargesPoints}
          fill="none"
          className="stroke-chart-4"
          strokeWidth="3"
        />

        {/* Collected line */}
        <polyline
          points={collectedPoints}
          fill="none"
          className="stroke-chart-2"
          strokeWidth="3"
        />

        {/* Data points */}
        {data.map((d, i) => {
          const x = padding + i * xStep;
          const yCharge = chartHeight - padding - d.totalCharges * yScale;
          const yCollected = chartHeight - padding - d.totalCollected * yScale;

          return (
            <g key={i}>
              <circle cx={x} cy={yCharge} r="4" className="fill-chart-4" />
              <circle cx={x} cy={yCollected} r="4" className="fill-chart-2" />
            </g>
          );
        })}

        {/* X-axis labels */}
        {data.map((d, i) => {
          const x = padding + i * xStep;
          return (
            <text
              key={i}
              x={x}
              y={chartHeight - padding + 20}
              textAnchor="middle"
              className="fill-muted-foreground"
              fontSize="12"
            >
              {d.month}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
